import { PayloadAction } from '@reduxjs/toolkit';
import {
    call,
    put,
    select,
    take,
    takeEvery,
    takeLeading,
} from 'redux-saga/effects';
import { END } from 'redux-saga';

import { fetchDocumentDetailsApi } from './docUploadApi';
import {
    selectDocumentsUrl,
    selectDocUploadCMS,
    selectMandatoryCategories,
    selectMaxTotalFilesSize,
    selectRequiredCategories,
    selectUpdateDocumentsUrl,
    selectUploadedDocuments,
    selectValidationsCMS,
} from './docUploadSelectors';

import {
    deleteAlreadyUploadedOption,
    deleteUploadedFile,
    getDocuments,
    getDocumentsFulfilled,
    getDocumentsRejected,
    getDocUploadDataInvalid,
    updateAlreadyUploadedOption,
    updateDocumentInBrowser,
    updateDocumentInBrowserFullfilled,
    updateDocumentInBrowserRejected,
    updateDocuments,
    updateDocumentsFulfilled,
    updateDocumentsRejected,
    updateSessionStatus,
} from './docUploadSlice';

import {
    DocUploadAEMValidations,
    DocUploadSubmit,
    DropdownMenu,
    UpdateDocuments,
    UpdateDocumentsInBrowser,
    UploadSessionStatus,
} from './docUploadTypes';
import {
    AddSessionInfoIntoData,
    checkDocUploadDataValidation,
    delay,
} from './utils';
import { createFileUploadChannel } from '../../../utils/createFileUploadChannel';
import { UploadError } from '../../../sharedComponents/UploadButton/types';

function* getDocumentsWorkSaga(action: PayloadAction<DocUploadSubmit>) {
    try {
        const url: string = yield select(selectDocumentsUrl);

        const { data } = yield call(fetchDocumentDetailsApi, url);

        if (checkDocUploadDataValidation(data)) {
            const formatData = AddSessionInfoIntoData(data);

            yield put(getDocumentsFulfilled(formatData));
        } else {            
            yield put(getDocUploadDataInvalid({ ...action.payload, isDocUploadRequired: false } as DocUploadSubmit));
        }
    } catch (e) {
        yield put(getDocumentsRejected());
    }
}

function* getDocumentsWatchSaga() {
    yield takeLeading(getDocuments.type, getDocumentsWorkSaga);
}

function* updateDocumentWorkSaga(action: PayloadAction<UpdateDocuments>) {
    const errorAem: DocUploadAEMValidations  = yield select(selectValidationsCMS);
    const { sessionId, fileName, errorMsg, documentDetail } = action.payload;

    const UpdateData = {
        sessionId: sessionId,
        fileName: fileName,
        errorMsg: errorMsg,
        status: UploadSessionStatus.StartUploading,
        applicationNumber: documentDetail.applicationNumber,
        description: documentDetail.description,
        subCategory: documentDetail.subCategory,
        subCategoryId: documentDetail.subCategoryId,
        id: documentDetail.id,
        categoryId: documentDetail.categoryId,
        documentTypeId: documentDetail.documentTypeId,
        progress: 0,
    };

    try {
        const url: string = yield select(selectUpdateDocumentsUrl);

        const channel: number = yield call(
            createFileUploadChannel,
            url,
            action.payload,
        );

        yield put(updateSessionStatus(UpdateData));

        while (true) {
            // loop until completion or error
            const status: number | Error | XMLHttpRequest | END = yield take(channel);
            const isSuccess: boolean = typeof status === 'object' && Object.keys(status).includes('success');
            const isError: boolean = typeof status === 'object' && Object.keys(status).includes('error');

            if (isSuccess) {
                yield put(updateDocumentsFulfilled());

                yield put(updateSessionStatus(
                    Object.assign(UpdateData, {
                        status: UploadSessionStatus.SuccessfullyUploaded,
                    }),
                ));
            } else if (isError) {
                // error handler
                yield put(updateDocumentsRejected());

                yield put(updateSessionStatus(
                    Object.assign(UpdateData, {
                        status: UploadSessionStatus.FailedUpload,
                        errorMsg: errorAem.hardValidations[UploadError.UploadFailed],
                    }),
                ));
            } else {
                // If still in progress
                yield put(updateSessionStatus(
                    Object.assign(UpdateData, {
                        progress: status,
                    }),
                ));
            }
        }
    } catch (e) {
        yield put(updateDocumentsRejected());

        yield put(updateSessionStatus(
            Object.assign(UpdateData, {
                status: UploadSessionStatus.FailedUpload,
                errorMsg: errorAem.hardValidations[UploadError.UploadFailed],
            }),
        ));
    }
}

function* updateDocumentsWatchSaga() {
    yield takeEvery(updateDocuments.type, updateDocumentWorkSaga);
}

function* updateDocumentInBrowserWorkSaga(action: PayloadAction<UpdateDocuments>){
    const errorAem: DocUploadAEMValidations = yield select(selectValidationsCMS);
    const uploadedDocuments: UpdateDocumentsInBrowser[] = yield select(selectUploadedDocuments);
    const maxTotalFilesSize:number = yield select(selectMaxTotalFilesSize);
    const mandatoryCategories: string[] = yield select(selectMandatoryCategories);
    const { alreadyUploadOption } = yield select(selectDocUploadCMS);
    const { sessionId, fileName, errorMsg, documentDetail, file } = action.payload;
    const fileSize = file.size;
    const chunkSize = fileSize/5;
    let errorCaught = false;

    let bytesUploaded = 0;

    const UpdateData = {
        sessionId: sessionId,
        fileName: fileName,
        errorMsg: errorMsg,
        status: UploadSessionStatus.StartUploading,
        applicationNumber: documentDetail.applicationNumber,
        description: documentDetail.description,
        subCategory: documentDetail.subCategory,
        subCategoryId: documentDetail.subCategoryId,
        id: documentDetail.id,
        categoryId: documentDetail.categoryId,
        documentTypeId: documentDetail.documentTypeId,
        progress: 0,
    };
    const totalSize = ((uploadedDocuments.reduce((acc, doc) => acc + doc.fileSize, 0) + fileSize) / (1024*1024)).toFixed(2);

    function validateFileSize(size: string, totalFileSize: number) {
        return parseFloat(size) > totalFileSize;
    }

    if(validateFileSize(totalSize, maxTotalFilesSize)){
        
        yield put(updateSessionStatus(Object.assign(UpdateData, {
            status: UploadSessionStatus.OverMaximumFileSize,
            errorMsg: errorAem.hardValidations[UploadError.MaximumTotalFilesSize],
        })));
        yield put(updateDocumentInBrowserRejected());
        
        return;
    }

    try {
        while(bytesUploaded < fileSize){
            bytesUploaded += chunkSize;
            const progress = Math.min((bytesUploaded / fileSize ) * 100, 100);

            yield put(updateSessionStatus(Object.assign(UpdateData, {
                progress: progress,
            })));

            yield call(delay, 1000);
        }
    } catch (e) {
        yield put(updateDocumentsRejected());

        yield put(updateSessionStatus(
            Object.assign(UpdateData, {
                status: UploadSessionStatus.FailedUpload,
                errorMsg: errorAem.hardValidations[UploadError.UploadFailed],
                documentTypeId: '',
            }),
        ));

        errorCaught = true;
    }
    finally{
        if(!errorCaught){
            yield put(updateSessionStatus(Object.assign(UpdateData, {
                progress: 100,
            })));
            const updateDocInBrowser = {
                fileSize: action.payload.file.size,
                documentDetail: action.payload.documentDetail,
                errorMsg: action.payload.errorMsg,
                fileName: action.payload.fileName,
                sessionId: action.payload.sessionId,
            };

            yield put(updateDocumentInBrowserFullfilled(updateDocInBrowser));
            yield put(updateDocumentsFulfilled());
            yield put(updateSessionStatus(
                Object.assign(UpdateData, {
                    status: UploadSessionStatus.SuccessfullyUploaded,
                }),
            ));
            if(!mandatoryCategories.includes(documentDetail.categoryId)) {
                yield put(updateAlreadyUploadedOption({ docTipKey: alreadyUploadOption?.id,
                    id: alreadyUploadOption?.id, 
                    name: alreadyUploadOption?.value }));
            } 
        }
    }
}

function* updateDocumentInBrowserWatchSaga() {
    yield takeEvery(updateDocumentInBrowser.type, updateDocumentInBrowserWorkSaga);
}

function* deleteDocumentInBrowserWorkSaga(){
    const uploadedDocuments: UpdateDocumentsInBrowser[] = yield select(selectUploadedDocuments);
    const docUploadInfo: DropdownMenu[]  = yield select(selectRequiredCategories);
    const mandatoryCategories: string[] = yield select(selectMandatoryCategories);
    const { alreadyUploadOption } = yield select(selectDocUploadCMS);
    const maxTotalFilesSize:number = yield select(selectMaxTotalFilesSize);

    const totalSize = ((uploadedDocuments.reduce((acc, doc) => acc + doc.fileSize, 0)) / (1024*1024)).toFixed(2);

    if(parseFloat(totalSize) <= maxTotalFilesSize){
        const sessionToUpdate = docUploadInfo.flatMap(doc => doc.data.flatMap(data => 
            data.sessionInfo.filter(session => session.status === UploadSessionStatus.OverMaximumFileSize)
                .map(session =>({
                    ...session,
                    status: UploadSessionStatus.Initialize,
                    errorMsg: '',
                    categoryId: doc.categoryName,
                    id: data.id,
                    subCategory: data.subCategory,
                    subCategoryId: data.subCategoryId,
                }))));

        for(const session of sessionToUpdate){
            yield put(updateSessionStatus(session));
        }
    }

    if(uploadedDocuments.filter(doc => !mandatoryCategories.includes(doc.documentDetail.categoryId)).length > 0 ) {
        yield put(updateAlreadyUploadedOption({ docTipKey: alreadyUploadOption?.id,
            id: alreadyUploadOption?.id, 
            name: alreadyUploadOption?.value })); 
    } else {
        const optionToDelete = docUploadInfo.flatMap(doc => doc.data.map(data =>
            ({ categoryName: doc.categoryName, subCategory: data.subCategory })));

        for(const option of optionToDelete){
            yield put(deleteAlreadyUploadedOption(option));
        }
    }
}
function* deleteDocumentInBrowserWatchSaga() {
    yield takeEvery(deleteUploadedFile.type, deleteDocumentInBrowserWorkSaga);
}
export const docUploadSagas = [getDocumentsWatchSaga, updateDocumentsWatchSaga, updateDocumentInBrowserWatchSaga, deleteDocumentInBrowserWatchSaga];
